import { useEffect, useRef, useState } from 'react';
import { useGetAccountsQuery } from '../store/api/dashboardApi';
import { t } from '../i18n/i18n';

/** The API's page size — the largest it answers in one go. */
const PAGE_SIZE = 100;

/**
 * One page of the picker. Its own query, so pages already seen come from the
 * cache, and page 1 without a search is the very request the top bar already
 * made for the selected account.
 */
function AccountPage({ page, search, isLast, onMore, renderRow }) {
  const { data, isFetching, isError } = useGetAccountsQuery(
    search ? { page, page_size: PAGE_SIZE, search } : { page, page_size: PAGE_SIZE },
  );
  const rows = data?.accounts?.results ?? [];
  const total = data?.accounts?.count ?? rows.length;
  const hasMore = page * PAGE_SIZE < total;

  // The next page is asked for as the end of this one scrolls into view —
  // the browser clips the sentinel to the dropdown's own scroll box.
  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !isLast || !hasMore || isFetching) return undefined;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && onMore(), {
      rootMargin: '120px 0px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [isLast, hasMore, isFetching, onMore]);

  if (isFetching && !data) {
    return (
      <div className="topbar__account-item is-empty">
        {page === 1 ? (search ? t('Searching…') : t('Loading accounts…')) : t('Loading more…')}
      </div>
    );
  }

  if (isError) {
    return <div className="topbar__account-item is-empty">{t('Could not load accounts')}</div>;
  }

  if (page === 1 && rows.length === 0) {
    return (
      <div className="topbar__account-item is-empty">
        {search ? t('No accounts match “{{search}}”', { search }) : t('No accounts on this profile yet')}
      </div>
    );
  }

  return (
    <>
      {rows.map(renderRow)}
      {isLast && hasMore && <div ref={sentinel} className="topbar__account-sentinel" aria-hidden="true" />}
    </>
  );
}

/**
 * The account picker's list — N22: every account the profile has, not the
 * first hundred. Pages arrive a hundred at a time as the list is scrolled to
 * its end, the way the live accounts table pages; a search pages the same way.
 */
export default function AccountPickerList({ search, renderRow }) {
  const [pages, setPages] = useState(1);
  // A new search starts again from its own first page.
  useEffect(() => setPages(1), [search]);

  const [onMore] = useState(() => () => setPages((n) => n + 1));

  return Array.from({ length: pages }, (_, i) => (
    <AccountPage
      key={`${search}:${i + 1}`}
      page={i + 1}
      search={search}
      isLast={i + 1 === pages}
      onMore={onMore}
      renderRow={renderRow}
    />
  ));
}
